














import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Editor as TipTapViewer, Text } from 'tiptap';
import debounce from 'debounce';
import { Bold, Italic, Underline, LinkViewer, Strike } from '@/editor/tiptap/marks';
import {
  ListItemNode,
  BreakNode,
  BulletListNode,
  HeadingNode,
  OrderedListNode,
  ParagraphNode,
  TableCellNode,
  TableNode,
  TableHeaderNode,
  TableRowNode,
  TableDocNode,
  MergeValueNode,
} from '@/editor/tiptap/nodes';
import { EColors, EHeadingLevel, ENodeAlign, EProsemirrorType } from '@/editor/types/enums';
import ProsemirrorWrapper from '@/editor/editor-block/components/ProsemirrorWrapper.vue';
import { IField, IMergeValues } from '@/editor/types';
import { EditorHelper } from '@/editor/helpers/editor.helper';

@Component({
  components: {
    ProsemirrorWrapper,
  },
  data: () => ({
    EColors,
  }),
})
export default class ProsemirrorViewerTable extends Vue {
  @Prop() content: string;
  @Prop() borderColor: string;
  @Prop() alternateRowColor: string;
  @Prop() headerRowBackgroundColor: string;
  @Prop() mergeValues: IMergeValues;
  @Prop() isPdfViewer: boolean;
  @Prop() fields: IField[];
  @Watch('fields')
  @Watch('mergeValues')
  updateMergeValues() {
    const mergeTagNode = this.viewer.extensions.extensions.find(
      ext => ext.name === EProsemirrorType.MergeTag
    );
    if (!mergeTagNode) {
      return;
    }
    mergeTagNode.options.mergeValues = this.mergeValues;
    mergeTagNode.options.fields = this.fields;
  }
  @Watch('content') setContent() {
    if (JSON.stringify(this.viewer.getJSON()) === JSON.stringify(this.content)) {
      return;
    }
    this.viewer.setContent(this.content);
  }

  debounceHandleScroll: () => void = debounce(this.setOverflow, 300);
  overflow = false;
  tableWrapper: HTMLElement = undefined;
  viewer: TipTapViewer | null = null;

  get tableViewer() {
    return {
      content: this.content,
      editable: false,
      disableInputRules: false,
      disablePasteRules: false,
      useBuiltInExtensions: false,
      extensions: [
        new TableDocNode(),
        new ParagraphNode({ defaultAlign: ENodeAlign.Left }),
        new Text(),
        new Bold(),
        new Italic(),
        new Underline(),
        new Strike(),
        new OrderedListNode(),
        new BulletListNode(),
        new ListItemNode(),
        new MergeValueNode({
          mergeValues: this.mergeValues,
          fields: this.fields,
        }),
        new LinkViewer(),
        new TableNode({
          resizable: !this.isPdfViewer,
          resizeOptions: {
            cellMinWidth: 25,
          },
        }),
        new TableHeaderNode(),
        new TableCellNode(),
        new TableRowNode(),
        new HeadingNode({
          defaultLevel: EHeadingLevel.Heading1,
          defaultAlign: ENodeAlign.Left,
          levels: [
            EHeadingLevel.Heading1,
            EHeadingLevel.Heading2,
            EHeadingLevel.Subheading,
            EHeadingLevel.Label,
          ],
        }),
        new BreakNode(),
      ],
      dropCursor: {
        color: 'transparent',
      },
    };
  }

  setOverflow() {
    this.overflow = EditorHelper.hasHiddenContentRight(this.tableWrapper);
  }

  handleLoaded() {
    this.tableWrapper = (this.$refs.editorContainer as HTMLElement)?.querySelector('.tableWrapper');
    if (!this.tableWrapper) {
      return;
    }

    this.tableWrapper?.addEventListener('scroll', this.debounceHandleScroll);
    this.setOverflow();
  }

  mounted() {
    this.viewer = new TipTapViewer(this.tableViewer);
  }

  beforeDestroy() {
    this.tableWrapper?.removeEventListener('scroll', this.debounceHandleScroll);
    this.viewer.destroy();
  }
}
